import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  getDocs,
  Timestamp
} from "firebase/firestore";
import { db } from "./firebase";
import { ClickableStudioName } from "./utils/ClickableNames";

export default function StudioRankings() {
  const [loading, setLoading] = useState(true);
  const [studioStats, setStudioStats] = useState([]);
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedDiscipline, setSelectedDiscipline] = useState("all");
  const [availableYears, setAvailableYears] = useState([]);
  const [availableDisciplines, setAvailableDisciplines] = useState([]);

  // 🗓️ Friendly date formatter
  function formatDate(ts) {
    const dateObj =
      ts instanceof Timestamp
        ? ts.toDate()
        : typeof ts === "string"
        ? new Date(ts)
        : ts;

    return dateObj.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  }

  // 📊 Load and analyze studio performance data
  const loadStudioData = async () => {
    setLoading(true);
    const studioMap = new Map();
    const years = new Set();
    const disciplines = new Set();

    try {
      // Query old structure
      const oldQuery = query(collection(db, "competitions"));
      const oldSnapshot = await getDocs(oldQuery);
      
      oldSnapshot.forEach((doc) => {
        const comp = doc.data();
        const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
        const year = compDate.getFullYear();
        
        years.add(year);
        if (comp.discipline) disciplines.add(comp.discipline);
        
        if (comp.results) {
          comp.results.forEach((result) => {
            const studio = result.studio || "Unknown Studio";
            const place = result.place;
            const discipline = comp.discipline || "Unknown";
            
            // Only count podium finishes (1st, 2nd, 3rd place)
            if (place >= 1 && place <= 3) {
              // Apply filters
              const yearMatch = selectedYear === "all" || year === parseInt(selectedYear);
              const disciplineMatch = selectedDiscipline === "all" || discipline === selectedDiscipline;
              
              if (yearMatch && disciplineMatch) {
                if (!studioMap.has(studio)) {
                  studioMap.set(studio, {
                    name: studio,
                    first: 0,
                    second: 0,
                    third: 0,
                    total: 0,
                    competitions: new Set()
                  });
                }
                
                const studioData = studioMap.get(studio);
                studioData.competitions.add(doc.id);
                
                if (place === 1) studioData.first++;
                else if (place === 2) studioData.second++;
                else if (place === 3) studioData.third++;
                
                studioData.total++;
              }
            }
          });
        }
      });

      // Query new structure
      const disciplinesSnapshot = await getDocs(collection(db, "disciplines"));
      
      for (const disciplineDoc of disciplinesSnapshot.docs) {
        const disciplineName = disciplineDoc.id;
        disciplines.add(disciplineName);
        
        const categoriesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories"));
        
        for (const categoryDoc of categoriesSnapshot.docs) {
          const categoryName = categoryDoc.id;
          
          // Check direct competitions
          try {
            const directCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "competitions"));
            
            directCompsSnapshot.forEach((doc) => {
              const comp = doc.data();
              const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
              const year = compDate.getFullYear();
              
              years.add(year);
              
              if (comp.results) {
                comp.results.forEach((result) => {
                  const studio = result.studio || "Unknown Studio";
                  const place = result.place;
                  const discipline = comp.discipline || disciplineName;
                  
                  if (place >= 1 && place <= 3) {
                    const yearMatch = selectedYear === "all" || year === parseInt(selectedYear);
                    const disciplineMatch = selectedDiscipline === "all" || discipline === selectedDiscipline;
                    
                    if (yearMatch && disciplineMatch) {
                      if (!studioMap.has(studio)) {
                        studioMap.set(studio, {
                          name: studio,
                          first: 0,
                          second: 0,
                          third: 0,
                          total: 0,
                          competitions: new Set()
                        });
                      }
                      
                      const studioData = studioMap.get(studio);
                      studioData.competitions.add(doc.id);
                      
                      if (place === 1) studioData.first++;
                      else if (place === 2) studioData.second++;
                      else if (place === 3) studioData.third++;
                      
                      studioData.total++;
                    }
                  }
                });
              }
            });
          } catch (error) {
            // Collection might not exist
          }
          
          // Check dance-specific competitions
          try {
            const dancesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances"));
            
            for (const danceDoc of dancesSnapshot.docs) {
              const danceName = danceDoc.id;
              const danceCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions"));
              
              danceCompsSnapshot.forEach((doc) => {
                const comp = doc.data();
                const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
                const year = compDate.getFullYear();
                
                years.add(year);
                
                if (comp.results) {
                  comp.results.forEach((result) => {
                    const studio = result.studio || "Unknown Studio";
                    const place = result.place;
                    const discipline = comp.discipline || disciplineName;
                    
                    if (place >= 1 && place <= 3) {
                      const yearMatch = selectedYear === "all" || year === parseInt(selectedYear);
                      const disciplineMatch = selectedDiscipline === "all" || discipline === selectedDiscipline;
                      
                      if (yearMatch && disciplineMatch) {
                        if (!studioMap.has(studio)) {
                          studioMap.set(studio, {
                            name: studio,
                            first: 0,
                            second: 0,
                            third: 0,
                            total: 0,
                            competitions: new Set()
                          });
                        }
                        
                        const studioData = studioMap.get(studio);
                        studioData.competitions.add(doc.id);
                        
                        if (place === 1) studioData.first++;
                        else if (place === 2) studioData.second++;
                        else if (place === 3) studioData.third++;
                        
                        studioData.total++;
                      }
                    }
                  });
                }
              });
            }
          } catch (error) {
            // No dances for this category
          }
        }
      }

      // Convert to array and sort by total podium finishes
      const studioArray = Array.from(studioMap.values()).map(studio => ({
        ...studio,
        competitionCount: studio.competitions.size
      })).sort((a, b) => {
        // Sort by first places first, then second, then third, then total
        if (a.first !== b.first) return b.first - a.first;
        if (a.second !== b.second) return b.second - a.second;
        if (a.third !== b.third) return b.third - a.third;
        return b.total - a.total;
      });

      setStudioStats(studioArray);
      setAvailableYears(Array.from(years).sort((a, b) => b - a));
      setAvailableDisciplines(Array.from(disciplines).sort());
      
    } catch (error) {
      console.error("❌ Error loading studio data:", error);
    }
    
    setLoading(false);
  };

  useEffect(() => {
    loadStudioData();
  }, [selectedYear, selectedDiscipline]);

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>🏆 Studio Rankings</h2>
        <p>Loading studio performance data...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem", maxWidth: "1200px", margin: "0 auto" }}>
      <h2>🏆 Studio Rankings by Podium Finishes</h2>
      <p style={{ color: "#666", marginBottom: "2rem" }}>
        Rankings based on 1st, 2nd, and 3rd place finishes across all competitions.
        Studios are ranked primarily by gold medals (1st place), then silver (2nd), then bronze (3rd).
      </p>

      {/* Filter Controls */}
      <div style={{ 
        marginBottom: "2rem", 
        padding: "1rem", 
        backgroundColor: "#f5f5f5", 
        borderRadius: "8px",
        display: "flex",
        gap: "1rem",
        flexWrap: "wrap"
      }}>
        <label style={{ display: "flex", flexDirection: "column", minWidth: "150px" }}>
          <strong>Year:</strong>
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(e.target.value)}
            style={{ padding: "0.5rem", marginTop: "0.25rem" }}
          >
            <option value="all">All Years</option>
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </label>
        
        <label style={{ display: "flex", flexDirection: "column", minWidth: "150px" }}>
          <strong>Discipline:</strong>
          <select 
            value={selectedDiscipline} 
            onChange={(e) => setSelectedDiscipline(e.target.value)}
            style={{ padding: "0.5rem", marginTop: "0.25rem" }}
          >
            <option value="all">All Disciplines</option>
            {availableDisciplines.map(discipline => (
              <option key={discipline} value={discipline}>{discipline}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Results Summary */}
      <div style={{ marginBottom: "1rem", color: "#666" }}>
        <p>
          📊 Showing results for: {selectedYear === "all" ? "All Years" : selectedYear} | 
          {selectedDiscipline === "all" ? " All Disciplines" : ` ${selectedDiscipline}`} | 
          Studios with podium finishes: {studioStats.length}
        </p>
      </div>

      {/* Studio Rankings Table */}
      {studioStats.length > 0 ? (
        <div className="studio-rankings-table-container">
          <table className="studio-rankings-table">
            <thead>
              <tr>
                <th className="rank-column">Rank</th>
                <th className="studio-name-column">Studio</th>
                <th className="medal-column gold">🥇</th>
                <th className="medal-column silver">🥈</th>
                <th className="medal-column bronze">🥉</th>
                <th className="desktop-only total-column">Total Podiums</th>
                <th className="desktop-only competitions-column">Competitions</th>
              </tr>
            </thead>
            <tbody>
              {studioStats.map((studio, index) => (
                <tr key={studio.name} className={`studio-row ${index < 3 ? 'podium-row' : ''}`}>
                  <td className="rank-column">
                    {index === 0 && "🥇"}
                    {index === 1 && "🥈"}
                    {index === 2 && "🥉"}
                    {index > 2 && `#${index + 1}`}
                  </td>
                  <td className="studio-name-column">
                    <ClickableStudioName name={studio.name} />
                  </td>
                  <td className="medal-column gold">
                    {studio.first}
                  </td>
                  <td className="medal-column silver">
                    {studio.second}
                  </td>
                  <td className="medal-column bronze">
                    {studio.third}
                  </td>
                  <td className="desktop-only total-column">
                    {studio.total}
                  </td>
                  <td className="desktop-only competitions-column">
                    {studio.competitionCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ 
          textAlign: "center", 
          padding: "3rem", 
          color: "#666",
          backgroundColor: "#f9f9f9",
          borderRadius: "8px"
        }}>
          <p>No podium finishes found for the selected filters.</p>
        </div>
      )}

      {/* Additional Statistics */}
      {studioStats.length > 0 && (
        <div style={{ 
          marginTop: "2rem", 
          padding: "1rem", 
          backgroundColor: "#e3f2fd", 
          borderRadius: "8px" 
        }}>
          <h4>📈 Summary Statistics</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
            <div>
              <strong>Total Studios:</strong> {studioStats.length}
            </div>
            <div>
              <strong>Total Gold Medals:</strong> {studioStats.reduce((sum, s) => sum + s.first, 0)}
            </div>
            <div>
              <strong>Total Silver Medals:</strong> {studioStats.reduce((sum, s) => sum + s.second, 0)}
            </div>
            <div>
              <strong>Total Bronze Medals:</strong> {studioStats.reduce((sum, s) => sum + s.third, 0)}
            </div>
            <div>
              <strong>Total Podium Finishes:</strong> {studioStats.reduce((sum, s) => sum + s.total, 0)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
